import i18next from "i18next";
import { AnyAction } from "redux";
import { all, call, put, takeEvery, takeLatest } from "redux-saga/effects";
import { isApiError, requestPuppy } from "@wix/da-http-client";
import { putErrorNotification } from "@wix/da-shared-react/pkg/utils/saga";
import {
  launchFeature,
  launchFeatureFailure,
  launchFeatureSuccess,
  reloadFeaturesInit,
  reloadFeaturesInitFailure,
  reloadFeaturesInitSuccess,
  resetFeatures,
  resetFeaturesFailure,
  resetFeaturesSuccess,
  toggleFavourite,
  toggleFavouriteFailure,
  toggleFavouriteSuccess,
  toggleFeature,
  toggleFeatureFailure,
  toggleFeatureSuccess,
  unlaunchFeature,
  unlaunchFeatureFailure,
  unlaunchFeatureSuccess,
} from "./actions";
import { FeaturesInitData } from "./types";

const FEATURES_PAPI_BASE = "features";

function* showActionError(errorMessage: string) {
  yield putErrorNotification(i18next.t(errorMessage));
}

function isInitResponse(data: any): data is FeaturesInitData {
  return (
    !!data &&
    typeof data === "object" &&
    Array.isArray(data.groups) &&
    !!data.featureCountBanner &&
    typeof data.featureCountBanner === "object"
  );
}

export function* requestFeaturesInit() {
  const response = yield call(
    requestPuppy,
    {
      method: "GET",
      url: "init",
    },
    undefined,
    FEATURES_PAPI_BASE
  );

  if (!response || isApiError(response) || !isInitResponse(response)) {
    const errorMessage = "Could not reload features list.";
    yield put(reloadFeaturesInitFailure(errorMessage));
    yield* showActionError(errorMessage);
    return;
  }

  yield put(
    reloadFeaturesInitSuccess({
      featureCountBanner: response.featureCountBanner,
      groups: response.groups,
    })
  );
}

function* runMutation({
  url,
  data,
  onSuccess,
  onFailure,
  errorMessage,
}: {
  url: string;
  data?: object;
  onSuccess: () => AnyAction;
  onFailure: (msg: string) => AnyAction;
  errorMessage: string;
}) {
  const response = yield call(
    requestPuppy,
    { method: "POST", url, data },
    undefined,
    FEATURES_PAPI_BASE
  );

  if (!response || isApiError(response) || !response.success) {
    yield put(onFailure(errorMessage));
    yield* showActionError(errorMessage);
    return;
  }

  yield put(onSuccess());
  yield put(reloadFeaturesInit());
}

export function* handleToggleFeature(action: ReturnType<typeof toggleFeature>) {
  yield* runMutation({
    url: "toggle",
    data: action.payload,
    onSuccess: () => toggleFeatureSuccess(),
    onFailure: (msg) => toggleFeatureFailure(msg),
    errorMessage: "Could not toggle feature.",
  });
}

export function* handleResetFeatures() {
  yield* runMutation({
    url: "reset",
    data: {},
    onSuccess: () => resetFeaturesSuccess(),
    onFailure: (msg) => resetFeaturesFailure(msg),
    errorMessage: "Could not reset features.",
  });
}

export function* handleLaunchFeature(action: ReturnType<typeof launchFeature>) {
  yield* runMutation({
    url: "launch",
    data: action.payload,
    onSuccess: () => launchFeatureSuccess(),
    onFailure: (msg) => launchFeatureFailure(msg),
    errorMessage: "Could not launch feature.",
  });
}

export function* handleUnlaunchFeature(action: ReturnType<typeof unlaunchFeature>) {
  yield* runMutation({
    url: "unlaunch",
    data: action.payload,
    onSuccess: () => unlaunchFeatureSuccess(),
    onFailure: (msg) => unlaunchFeatureFailure(msg),
    errorMessage: "Could not unlaunch feature.",
  });
}

export function* handleToggleFavourite(action: ReturnType<typeof toggleFavourite>) {
  yield* runMutation({
    url: "toggle_fave",
    data: action.payload,
    onSuccess: () => toggleFavouriteSuccess(),
    onFailure: (msg) => toggleFavouriteFailure(msg),
    errorMessage: "Could not update favourite feature.",
  });
}

export function* featuresSaga() {
  yield all([
    takeLatest(reloadFeaturesInit, requestFeaturesInit),
    takeEvery(toggleFeature, handleToggleFeature),
    takeEvery(resetFeatures, handleResetFeatures),
    takeEvery(launchFeature, handleLaunchFeature),
    takeEvery(unlaunchFeature, handleUnlaunchFeature),
    takeEvery(toggleFavourite, handleToggleFavourite),
  ]);
}
